import moment from "moment";

import { eventStatuses } from "../../../../utils/Constants";
import {
  EVENT_MODELS,
  PAYMENT_PERIODS,
  getEventBillingModel,
  getEventCrewSizeOption,
  getOperatingDays,
  getTotalApplicants,
  toCrewSizeOption,
} from "../../Form/Steps/ChooseCrewSize/crewSizeUtils";
import {
  PAYMENT_REQUEST_STATUSES,
  isUnpublishedEvent,
} from "../BasicInfo/paymentStateUtils";

export const getEventRoles = (event) =>
  event?.event_roles ?? event?.event_roles_attributes ?? [];

/**
 * Used organizers, ceiling and crew size of a published event; null when there is nothing to show.
 *
 * The ceiling is the event's crew size plan (`max_size`, or `custom_crew_size` on the
 * custom plan); events created before the payment flow have no plan and show nothing.
 */
export const getEventCapacity = (event) => {
  // Draft / Pending events have no banner; a published one does, even after its accepted
  // publish request (which the Basic Info card shows as "approved").
  if (!event?.id || isUnpublishedEvent(event)) return null;
  const used = getTotalApplicants(getEventRoles(event));
  const crewSize = getEventCrewSizeOption(event);
  if (!crewSize) return null;
  return { used, ceiling: crewSize.max, crewSize };
};

/**
 * Upgrade options from `crew_size_plans?is_upgrade=true` (PROLINE-4060): the backend lists
 * the fixed plans above the current ceiling and prices each one in `upgrade_cost`.
 */
export const toUpgradeOptions = (plans = []) =>
  plans
    .filter((plan) => !plan.custom)
    .map((plan) => ({
      ...toCrewSizeOption(plan),
      upgradeCost:
        plan.upgrade_cost == null || plan.upgrade_cost === ""
          ? null
          : Number(plan.upgrade_cost),
    }));

/** Operating days left, counting today, out of the event's operating days. */
export const getRemainingDays = (startAt, endAt, now = new Date()) => {
  const total = getOperatingDays(startAt, endAt);
  const from = moment.max(moment(now), moment(startAt));
  return { total, remaining: getOperatingDays(from, endAt) };
};

/**
 * Remaining days of an event, as the backend returns them (`remaining_operating_days` /
 * `operating_days`, PROLINE-4060); never counted here.
 */
export const getEventRemainingDays = (event) => ({
  total: event?.operating_days ?? null,
  remaining: event?.remaining_operating_days ?? null,
});

/**
 * The company may ask for a bigger crew size only on an Upcoming / Ongoing event with
 * operating days left; the backend refuses the rest (PROLINE-4060).
 */
export const isUpgradeableEvent = (event) =>
  [eventStatuses.upcoming, eventStatuses.ongoing].includes(event?.status) &&
  getEventRemainingDays(event).remaining > 0;

/** Billing of a published event follows `payment_plan`, frozen at publishing (not `payment_period`). */
export const getUpgradeBillingModel = (event) => {
  if (event?.payment_plan === PAYMENT_PERIODS.monthly) return EVENT_MODELS.extended;
  if (event?.payment_plan === PAYMENT_PERIODS.daily) return EVENT_MODELS.daily;
  return getEventBillingModel(event, event?.operating_days);
};

/** The event's latest crew size change request (`last_upgrade_payment_request`), or null. */
export const getUpgradeRequest = (event) => {
  const request = event?.last_upgrade_payment_request ?? null;
  // A cancelled request leaves nothing to show.
  return request && request.status !== PAYMENT_REQUEST_STATUSES.canceled
    ? request
    : null;
};
